import { ChartData, ChartOptions, Chart, Plugin } from 'chart.js';
import createLegendConfig, {
    createLegendOnHover,
    createLegendOnLeave,
} from './legend';
import { createTooltipCallbacks } from './tooltip';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface BarChartProps {
    data: ChartData<'bar'>;
    options?: ChartOptions<'bar'>;
    plugins?: Plugin<'bar'>[];
    testId?: string;
    fixedHeight?: boolean;
    minHeight?: number;
    maxHeight?: number;
}

export interface LineChartProps {
    data: ChartData<'line'>;
    options?: ChartOptions<'line'>;
    testId?: string;
    fixedHeight?: boolean;
    minHeight?: number;
    maxHeight?: number;
    heightPx?: number;
}

export interface DoughnutChartProps {
    data: ChartData<'doughnut'>;
    options?: ChartOptions<'doughnut'>;
    plugins?: unknown[];
    testId?: string;
    heightPx?: number;
    centerLayout?: boolean;
}

export interface MixedChartProps {
    type: 'bar' | 'line' | 'mixed';
    data: ChartData<'bar' | 'line'>;
    options?: ChartOptions<'bar' | 'line'>;
    testId?: string;
    fixedHeight?: boolean;
    minHeight?: number;
    maxHeight?: number;
    heightPx?: number;
}

export interface IPipeSizingChartOptions {
    chartTitle: string;
    mode: 'costs' | 'length';
    lengthSymbol: string;
    costSymbol: string;
    t: TranslateFn;
    legendPosition?: 'top' | 'right' | 'bottom';
}

export const getPipeSizingChartOptions = ({
    chartTitle,
    mode,
    lengthSymbol,
    costSymbol,
    t,
    legendPosition = 'right',
}: IPipeSizingChartOptions): ChartOptions<'bar'> => {
    return {
        responsive: true,
        plugins: {
            legend: {
                reverse: true,
                position: legendPosition,
                ...createLegendConfig(),
                onHover: createLegendOnHover,
                onLeave: createLegendOnLeave,
            },
            title: {
                display: true,
                text: chartTitle,
            },
            tooltip: {
                callbacks: createTooltipCallbacks({
                    t,
                    chartType: 'bar',
                    xLabelKey: t('Nominal diameter'),
                    yLabelKey:
                        mode === 'costs'
                            ? t('Costs (in EUR)', { symbol: costSymbol })
                            : t('Real length', { symbol: lengthSymbol }),
                    useDatasetLabelForY: false,
                }),
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: t('Nominal diameter'),
                },
            },
            y: {
                title: {
                    display: true,
                    text:
                        mode === 'costs'
                            ? t('Costs (in EUR)', { symbol: costSymbol })
                            : t('Real length', { symbol: lengthSymbol }),
                },
                beginAtZero: true,
            },
        },
    };
};

export const getChartDefaultsOptions = (
    fixedHeight: boolean,
    customOptions?: Partial<ChartOptions<'bar'>>
): ChartOptions<'bar'> => {
    const { plugins: customPlugins, ...otherOptions } = customOptions || {};

    const defaultPlugins = {
        legend: {
            display: true,
            position: 'top' as const,
        },
        title: {
            display: false,
        },
    };

    const mergedPlugins = { ...defaultPlugins, ...customPlugins };

    return {
        responsive: true,
        maintainAspectRatio: !fixedHeight,
        ...(fixedHeight ? {} : { aspectRatio: 2 }),
        plugins: mergedPlugins,
        ...otherOptions,
    };
};

// NOTE: chartjs-plugin-zoom is not installed in prototypes-studio.
// createZoomConfig kept as a no-op stub so callers don't break.
export const createZoomConfig = () => ({});

export const STACKED_BAR_DATASETS_CONFIG = {
    bar: {
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        maxBarThickness: 50,
    },
} as const;

// Re-export for convenience
export type { Chart };
